/**
 * Back in stock signup.
 *
 * Posts the shopper's email to Klaviyo's public client endpoint for
 * back-in-stock subscriptions. The endpoint takes the public company ID
 * (the six character public API key) and the Shopify variant ID in
 * Klaviyo's catalog format. Klaviyo answers 202 with an empty body when
 * the request is queued. It answers 404 when the variant is not in the
 * Klaviyo catalog yet, and 400 for a bad email or company ID.
 *
 * Klaviyo stores the request only. The email goes out from the Back in
 * Stock flow inside the Klaviyo account, so that flow has to be live.
 *
 * The Liquid block sets `hidden` on this element while the variant can be
 * bought. Horizon's variant picker refetches the section on every variant
 * change and fires variant:update on the section with the new document in
 * event.detail.data.html. Horizon only morphs its own pieces (buy buttons,
 * price, media), so this element swaps itself for the freshly rendered
 * copy, matched by data-block-id. Same approach as the subscription
 * widget. The variant ID is read from the attribute at submit time.
 */

// No trailing slash: Klaviyo redirects the slash form and a redirected
// cross-origin POST loses its body in the browser.
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
const KLAVIYO_REVISION = '2026-07-15';

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-back-in-stock-form]');
    this.input = this.querySelector('[data-back-in-stock-email]');
    this.button = this.querySelector('[data-back-in-stock-submit]');
    this.status = this.querySelector('[data-back-in-stock-status]');

    if (!this.form || this.form.dataset.bound === 'true') return;
    this.form.dataset.bound = 'true';
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.input?.addEventListener('input', () => this.clearStatus());

    // Card version (snippets/back-in-stock-card.liquid). Starts collapsed
    // to a single NOTIFY ME button; the click reveals the email row. The
    // button and the form live inside a product card, so the click must
    // not bubble up into the card's own click handling.
    this.openButton = this.querySelector('[data-back-in-stock-open]');
    this.openButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.expand();
    });

    this.abortController = new AbortController();
    const eventTarget = this.closest('.shopify-section, dialog') || document;
    eventTarget.addEventListener('variant:update', this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    const blockId = this.dataset.blockId;
    if (!html || !blockId) return;

    const replacement = html.querySelector(`back-in-stock-form[data-block-id="${blockId}"]`);
    if (!replacement) return;

    const nextNode = replacement.cloneNode(true);
    // Keep whatever the shopper had typed so a size change does not wipe it.
    const typed = this.input?.value || '';
    const nextInput = nextNode.querySelector('[data-back-in-stock-email]');
    if (nextInput && typed) nextInput.value = typed;
    this.replaceWith(nextNode);
  };

  expand() {
    if (!this.hasAttribute('data-collapsed')) return;
    this.removeAttribute('data-collapsed');
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.input?.focus();
  }

  get companyId() {
    return (this.dataset.companyId || '').trim();
  }

  get variantId() {
    return (this.dataset.variantId || '').trim();
  }

  clearStatus() {
    if (!this.status) return;
    this.status.textContent = '';
    this.status.removeAttribute('data-state');
    this.input?.removeAttribute('aria-invalid');
  }

  setStatus(state, text) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.dataset.state = state;
  }

  setBusy(isBusy) {
    this.toggleAttribute('data-busy', isBusy);
    if (this.button) this.button.disabled = isBusy;
    if (this.input) this.input.readOnly = isBusy;
  }

  isValidEmail(value) {
    // Same shape check browsers apply to type="email": one @, no spaces,
    // a dot in the domain. Klaviyo does the real validation server-side.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.hasAttribute('data-busy')) return;

    const email = (this.input?.value || '').trim();
    if (!this.isValidEmail(email)) {
      this.input?.setAttribute('aria-invalid', 'true');
      this.setStatus('invalid', this.status?.dataset.invalidText || 'Enter a valid email address.');
      this.input?.focus();
      return;
    }

    if (!this.companyId || !this.variantId) {
      this.setStatus('error', this.status?.dataset.errorText || 'Something went wrong. Please try again.');
      return;
    }

    this.setBusy(true);
    this.clearStatus();

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.variantId}`,
                },
              },
            },
          },
        }),
      });

      if (response.status === 202 || response.ok) {
        this.setStatus('success', this.status?.dataset.successText || 'You are on the list.');
        this.form.hidden = true;
        this.dispatchEvent(
          new CustomEvent('back-in-stock:subscribed', {
            bubbles: true,
            detail: { email, variantId: this.variantId },
          })
        );
        return;
      }

      let detail = '';
      try {
        const payload = await response.json();
        detail = payload?.errors?.[0]?.detail || '';
      } catch (_) {
        // Non-JSON body. Fall through to the generic message.
      }
      console.warn('[back-in-stock] Klaviyo rejected the request', response.status, detail);
      this.setStatus('error', this.status?.dataset.errorText || 'Something went wrong. Please try again.');
    } catch (error) {
      console.warn('[back-in-stock] request failed', error);
      this.setStatus('error', this.status?.dataset.errorText || 'Something went wrong. Please try again.');
    } finally {
      this.setBusy(false);
    }
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
